import { IOperationJson, Operation } from "./operation";
import { models, SearchOperationMapping } from "./searches";
import {
  eSearchFilterType,
  ILabeledField,
  RawFilters,
  SearchFilterOperationModel,
  toLabeledField,
} from "./searches/search-filter-operation-model";

// Re-export the shared primitives so existing `"operations/search-operation"`
// imports keep working. The base classes/values now live in a module that does
// NOT depend on `./searches`, which breaks the init-time circular dependency
// (search-operation -> searches -> *-operation-model -> search-operation).
export { eSearchFilterType as eSearchType, toLabeledField, SearchFilterOperationModel as SearchOperationModel };
export type { ILabeledField, RawFilters };

type HarFilters<N extends SearchFilterName> =
  SearchOperationMapping[N] extends SearchFilterOperationModel<infer H, any, any> ? H : never;

type Filters<N extends SearchFilterName>
  = SearchOperationMapping[N] extends SearchFilterOperationModel<any, infer T, any> ? T : never;

type ToolInput<N extends SearchFilterName>
  = SearchOperationMapping[N] extends SearchFilterOperationModel<any, any, infer T> ? T : never;

export interface ISearchFilterOperationArtifact<N extends SearchFilterName>
{
  search_id: string;
  search_name: N;
  filters: ToolInput<N>;
  artifact_text?: string;
}

export type SearchFilterName = keyof SearchOperationMapping;

type Model<N extends SearchFilterName> = SearchFilterOperationModel<HarFilters<N>, Filters<N>, ToolInput<N>>;

export class SearchFilterOperation<N extends SearchFilterName> extends Operation<"search_filter">
{
  private readonly model: Model<N>;
  public readonly search_name: N;
  public readonly filters: ToolInput<N>;
  public readonly type: eSearchFilterType;
  

  public get operation_name() { return "search_filter" as const; }

  public static fromNative<N extends SearchFilterName>(search_name: N, filters: HarFilters<N>, type: eSearchFilterType): SearchFilterOperation<N> {
    const model = new models[search_name]() as unknown as Model<N>;
    const toolInput = model.fromNativeToToolInput(filters);
    return new SearchFilterOperation(search_name, toolInput, type);
  }

  public static fromFilters<N extends SearchFilterName>(search_name: N, filters: Filters<N>, type: eSearchFilterType): SearchFilterOperation<N> {
    const model = new models[search_name]() as unknown as Model<N>;
    const toolInput = model.fromFiltersToToolInput(filters);
    return new SearchFilterOperation(search_name, toolInput, type);
  }

  public constructor(
    search_name: N,
    filters: ToolInput<N>,
    type: eSearchFilterType,
  ) {
    super();
    this.search_name = search_name;
    this.model = new models[search_name]() as unknown as Model<N>;
    this.filters = this.model.normalizeFilters(filters, type);
    this.type = type;
  }

  public getUserMessage(): string
  {
    const func = this.type === eSearchFilterType.NextPage? this.model.getNextPageUserMessage : this.model.getSearchUserMessage;
    return func.call(this.model, this.filters, this.type);
  }

  public buildUserMetadata(): Record<string, unknown>
  {
    // Mirror the server artifact shape (search_filter_operation.py build_user_metadata):
    // { search_id, search_name, type, filters }. The component reads `artifact.filters`
    // (a ToolInput) via fromToolInputToUIFilters, so filters must be nested, not spread.
    return {
      search_id: this.model.id,
      search_name: this.search_name,
      type: this.type,
      filters: this.filters,
    };
  }

  public toJson(): SearchFilterOperation.Json<"search_filter", N> {
    return {
      operation_name: this.operation_name,
      search_name: this.search_name,
      filters: this.model.toRaw(this.filters),
      type: this.type,
    };
  }
}

export namespace SearchFilterOperation
{
  export interface Json<O extends string, N extends SearchFilterName> extends IOperationJson<O>
  {
    search_name: N;
    filters: RawFilters<ToolInput<N>>;
    type: eSearchFilterType;
  }

  // The abstract base for per-search models now lives in `./search-operation-model`
  // (re-exported above as `SearchOperationModel`) so this module can depend on the
  // `./searches` registry without a circular import.
}